import React from 'react';
import {
    serie, mediaMobile, lineaDiBase, nottiValide,
    NOTTI_PER_MEDIA, NOTTI_PER_BANDA, FINESTRA_MEDIA,
} from '../../utils/andamento';
import '../../styles/GraficoLongevity.css';
import '../../styles/GraficoLongevityConsole.css';

const soloData = (v) => String(v).slice(0, 10);

const dataBreve = (iso) => {
    const d = new Date(`${soloData(iso)}T00:00:00Z`);
    return isNaN(d) ? '' : d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', timeZone: 'UTC' });
};

/**
 * GraficoLongevity — andamento della variabilità cardiaca e del battito
 * notturno, disegnato a mano in SVG.
 *
 * PERCHE' LA MEDIA MOBILE E NON I VALORI
 * L'HRV notturno oscilla molto da una notte all'altra: una notte storta,
 * un bicchiere di vino, l'inizio di un'influenza. Il singolo valore non
 * dice niente e invita a leggere un significato in un rumore. La linea
 * che conta e' la media su sette giorni; i valori giornalieri restano
 * visibili, ma piu' chiari, perche' si veda quanto disperdono.
 *
 * NESSUNA ZONA ROSSA O VERDE, E NON E' UNA DIMENTICANZA
 * L'HRV non e' confrontabile fra persone: 35 ms puo' essere ottimo per
 * uno e preoccupante per un altro, e non esiste un intervallo di
 * riferimento come per la glicemia. Un semaforo su valori assoluti
 * produrrebbe allarmi falsi finche' chi legge smette di guardarlo, che e'
 * il modo peggiore in cui un avviso puo' fallire. L'unico confronto che
 * ha senso e' quello con se stessi: la banda grigia e' la linea di base
 * di QUESTA persona, e infatti e' grigia e non verde.
 *
 * NIENTE PUNTEGGI INVENTATI DA NOI
 * Polar ha il suo recupero notturno e lo si mostra col suo nome. Costruire
 * qui un indice sintetico nostro - "indice di recupero", "readiness" -
 * significherebbe produrre un valore clinico che nessuno ha validato.
 *
 * LE SOGLIE
 * Sotto le quattordici notti si mostrano solo i punti: una media su tre
 * giorni non e' una media. La banda della linea di base da trenta notti
 * in su, perche' sotto quella soglia la dispersione e' dominata dal caso.
 * Fino ad allora il riquadro dice quante notti mancano - "7 notti su 14"
 * e' un'informazione, un'area bianca sembra un guasto.
 */

/* ------------------------------------------------------------------ */

const L = 40;    // spazio a sinistra per i valori
const R = 8;
const ALTO = 12;
const BASSO = 20;
const H = 150;

const Pannello = ({ titolo, unita, colore, punti, xMin, xMax, banda }) => {
    const w = 640;
    const larghezza = w - L - R;
    const altezza = H - ALTO - BASSO;

    const valori = punti.map((p) => p.valore).filter(Number.isFinite);
    if (!valori.length) return null;

    // La scala comprende anche la banda, altrimenti la banda esce dal riquadro
    let min = Math.min(...valori, banda ? banda.media - banda.sd : Infinity);
    let max = Math.max(...valori, banda ? banda.media + banda.sd : -Infinity);
    const respiro = (max - min) * 0.12 || Math.max(1, max * 0.05);
    min -= respiro;
    max += respiro;

    const x = (gg) => L + (xMax === xMin ? larghezza / 2 : ((gg - xMin) / (xMax - xMin)) * larghezza);
    const y = (v) => ALTO + altezza - ((v - min) / (max - min)) * altezza;

    // La linea si spezza dove la media non e' calcolabile: unire due tratti
    // sopra un buco disegnerebbe una tendenza che non e' stata misurata.
    const tratti = [];
    let corrente = [];
    for (const p of punti) {
        if (Number.isFinite(p.media)) corrente.push(`${x(p.gg).toFixed(1)},${y(p.media).toFixed(1)}`);
        else if (corrente.length) { tratti.push(corrente); corrente = []; }
    }
    if (corrente.length) tratti.push(corrente);

    // Tre valori sull'asse: piu' di cosi' e' rumore su un riquadro di questa altezza
    const tacche = [min + respiro, (min + max) / 2, max - respiro];

    return (
        <div className="gl-pannello">
            <div className="gl-pannello-testa">
                <span className="gl-pallino" style={{ background: colore }} />
                {titolo} <span className="gl-unita">({unita})</span>
            </div>
            <svg viewBox={`0 0 ${w} ${H}`} className="gl-svg" role="img"
                 aria-label={`${titolo}, andamento in ${unita}`}>
                {banda && (
                    <>
                        <rect x={L} y={y(banda.media + banda.sd)} width={larghezza}
                              height={Math.max(1, y(banda.media - banda.sd) - y(banda.media + banda.sd))}
                              className="gl-banda" />
                        <line x1={L} x2={w - R} y1={y(banda.media)} y2={y(banda.media)}
                              className="gl-banda-media" />
                    </>
                )}

                {tacche.map((v, i) => (
                    <g key={i}>
                        <line x1={L} x2={w - R} y1={y(v)} y2={y(v)} className="gl-griglia" />
                        <text x={L - 6} y={y(v) + 3.5} textAnchor="end" className="gl-tacca">
                            {v.toLocaleString('it-IT', { maximumFractionDigits: 0 })}
                        </text>
                    </g>
                ))}

                {/* I valori di ogni notte: chiari, dietro alla linea */}
                {punti.filter((p) => Number.isFinite(p.valore)).map((p) => (
                    <circle key={p.gg} cx={x(p.gg)} cy={y(p.valore)} r="2.6"
                            fill={colore} opacity="0.32">
                        <title>{`${dataBreve(p.giorno)}: ${p.valore.toLocaleString('it-IT', { maximumFractionDigits: 1 })} ${unita}`}</title>
                    </circle>
                ))}

                {tratti.map((t, i) => (
                    <polyline key={i} points={t.join(' ')} fill="none"
                              stroke={colore} strokeWidth="2"
                              strokeLinecap="round" strokeLinejoin="round" />
                ))}

                <text x={L} y={H - 5} className="gl-tacca">{dataBreve(punti[0].giorno)}</text>
                <text x={w - R} y={H - 5} textAnchor="end" className="gl-tacca">
                    {dataBreve(punti[punti.length - 1].giorno)}
                </text>
            </svg>
        </div>
    );
};

/* ------------------------------------------------------------------ */

/**
 * @param recuperi  righe di polar_recuperi, dalla piu' recente
 */
const GraficoLongevity = ({ recuperi = [] }) => {
    // serie() ordina dal piu' vecchio: un grafico si legge da sinistra
    const righe = recuperi;

    const hrv = serie(righe, 'hrv_media');
    const battito = serie(righe, 'battito_medio');

    const notti = nottiValide(hrv);
    const conMedia = notti >= NOTTI_PER_MEDIA;

    if (!notti) return null;

    const tutti = [...hrv, ...battito].filter((p) => Number.isFinite(p.valore));
    const xMin = Math.min(...tutti.map((p) => p.gg));
    const xMax = Math.max(...tutti.map((p) => p.gg));

    const prepara = (punti) => (conMedia ? mediaMobile(punti) : punti.map((p) => ({ ...p, media: null })));
    const bandaDi = (punti) => lineaDiBase(punti);

    const bandaHrv = bandaDi(hrv);

    return (
        <div className="gl-wrap">
            <div className="gl-testa">
                <h3>Andamento</h3>
                <span className="gl-conteggio">
                    {notti} {notti === 1 ? 'notte registrata' : 'notti registrate'}
                </span>
            </div>

            {!conMedia && (
                <p className="gl-avviso">
                    <strong>{notti} {notti === 1 ? 'notte' : 'notti'} su {NOTTI_PER_MEDIA}.</strong>{' '}
                    Per ora si vedono i valori delle singole notti. La linea di tendenza
                    compare da {NOTTI_PER_MEDIA} notti in poi: su meno di così una media
                    seguirebbe il caso invece dell&apos;andamento.
                </p>
            )}

            {conMedia && notti < NOTTI_PER_BANDA && (
                <p className="gl-avviso">
                    <strong>{notti} notti su {NOTTI_PER_BANDA}.</strong> La banda della linea
                    di base personale compare da {NOTTI_PER_BANDA} notti in poi.
                </p>
            )}

            <Pannello titolo="Variabilità cardiaca" unita="ms" colore="#17a2a2"
                      punti={prepara(hrv)} xMin={xMin} xMax={xMax} banda={bandaHrv} />

            <Pannello titolo="Battito medio notturno" unita="bpm" colore="#c0392b"
                      punti={prepara(battito)} xMin={xMin} xMax={xMax} banda={bandaDi(battito)} />

            <p className="gl-legenda">
                I punti chiari sono le singole notti
                {conMedia && <>; la linea è la media dei {FINESTRA_MEDIA} giorni precedenti</>}
                {bandaHrv && (
                    <>; la fascia grigia è la linea di base di questa persona
                       — media e scarto tipico delle sue ultime {bandaHrv.notti} notti</>
                )}.
            </p>
            <p className="gl-legenda gl-legenda--nota">
                Non ci sono soglie di normalità perché per la variabilità cardiaca non
                esistono: il valore di una persona si confronta con il proprio storico,
                non con quello di altri.
            </p>
        </div>
    );
};

export default GraficoLongevity;
